/**
 * Banner impression / click tracking
 * Fires banner_impression (50% visible, once per pageview) and banner_click
 * via Shopify.analytics.publish (picked up by any Custom Pixel: Triple Whale,
 * GA4, Meta, etc.) and mirrors to window.dataLayer for GTM compatibility.
 */
(function () {
  'use strict';

  var firedImpressions = new Set();

  function getDevice() {
    if (window.matchMedia('(min-width: 1024px)').matches) return 'desktop';
    if (window.matchMedia('(min-width: 768px)').matches) return 'tablet';
    return 'mobile';
  }

  function getSite() {
    var host = window.location.hostname.toLowerCase();
    var path = window.location.pathname.toLowerCase();
    if (host.indexOf('color') !== -1 || path.indexOf('xmondo-color') !== -1) return 'color';
    return 'hair';
  }

  // --- Popup obstruction tracking -----------------------------------------
  // Watches the newsletter (15% off) popup for its "open" state so impression
  // events can be tagged with whether the popup was covering the banner.
  var popupActive = false;
  function watchPopup() {
    var popupEl = document.querySelector('[id^="newsletter-popup"], newsletter-popup, .newsletter-popup');
    if (!popupEl) return;
    var check = function () {
      popupActive =
        popupEl.classList.contains('is-open') ||
        popupEl.hasAttribute('open') ||
        popupEl.getAttribute('aria-hidden') === 'false';
    };
    check();
    new MutationObserver(check).observe(popupEl, {
      attributes: true,
      attributeFilter: ['class', 'open', 'aria-hidden'],
    });
  }

  function publish(eventName, payload) {
    payload.device = getDevice();
    payload.site = getSite();
    payload.popup_active = popupActive;
    payload.timestamp = new Date().toISOString();

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, payload);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  }

  function readBanner(el) {
    return {
      creative_id: el.dataset.creativeId || '',
      placement: el.dataset.placement || '',
      banner_role: el.dataset.bannerRole || '',
      destination_url: el.dataset.destinationUrl || '',
      promo_flag: el.dataset.promoFlag === 'true',
      variant: el.dataset.variant || '',
      campaign_name: el.dataset.campaignName || '',
      creative_start_date: el.dataset.creativeStartDate || '',
    };
  }

  // --- Persist last-clicked banner through checkout -----------------------
  // Stores attribution in sessionStorage AND writes it to cart attributes so
  // it lands on the order (Additional details) for downstream joins.
  function persistClickAttribution(banner) {
    try {
      sessionStorage.setItem('last_banner_click', JSON.stringify(banner));
    } catch (e) {}

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          last_banner_creative_id: banner.creative_id,
          last_banner_placement: banner.placement,
          last_banner_role: banner.banner_role,
          last_banner_campaign: banner.campaign_name,
        },
      }),
    }).catch(function () {});
  }

  // --- Impression tracking (IntersectionObserver, 50% threshold) ---------
  // Resilient to Shopify's AJAX section re-renders / theme-editor hot-reload,
  // which can replace banner DOM nodes after initial page load. A
  // MutationObserver watches for newly-inserted banners and attaches the
  // IntersectionObserver to those too, using a "data-banner-observed" marker
  // so the same element is never double-observed.
  var io = null;
  function getIO() {
    if (io || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var id = el.dataset.creativeId;
          if (firedImpressions.has(id)) return;
          firedImpressions.add(id);
          publish('banner_impression', readBanner(el));
          io.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );
    return io;
  }

  function observeBanner(el) {
    if (el.hasAttribute('data-banner-observed')) return;
    el.setAttribute('data-banner-observed', 'true');
    var observer = getIO();
    if (observer) observer.observe(el);
  }

  function scanForBanners(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-banner-tracking][data-creative-id]:not([data-creative-id=""])').forEach(observeBanner);
  }

  function initImpressions() {
    if (!('IntersectionObserver' in window)) return;

    scanForBanners();

    // Watch for section re-renders (hot-reload, AJAX cart, slideshow swaps)
    // inserting fresh banner markup, and pick those up too.
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // only elements
          if (node.matches && node.matches('[data-banner-tracking]')) {
            scanForBanners(node.parentNode || document);
          } else if (node.querySelector) {
            scanForBanners(node);
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // --- Click tracking -------------------------------------------------------
  function initClicks() {
    document.addEventListener('click', function (e) {
      var ctaLink = e.target.closest('.banner-tracking-cta');
      if (!ctaLink) return;

      var wrapper = ctaLink.closest('[data-banner-tracking][data-creative-id]');
      // CTA buttons rendered outside the media wrapper (heading/button block)
      // fall back to matching by nearest slide index.
      if (!wrapper) {
        var slide = ctaLink.closest('[data-index]');
        if (slide) {
          var idx = slide.getAttribute('data-index');
          wrapper = document.querySelector(
            '[data-banner-tracking][data-placement="' + (parseInt(idx, 10) + 1) + '"]'
          );
        }
      }
      if (!wrapper) return;

      var banner = readBanner(wrapper);
      publish('banner_click', banner);
      persistClickAttribution(banner);
    });
  }

  function init() {
    watchPopup();
    initImpressions();
    initClicks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();